/**
 * @file 到達度モデル YAML の検証付き parse。
 *
 * @what: yaml plugin が返す素の object を `AttainmentModel` に変換し、構造の不整合
 *   (参照切れの stage / domain / level / taxonomy、id 重複、空セル) を throw で止める。
 * @why: モデルは人が手で編む正本なので、typo や参照切れをビルド時 (Vitest) と
 *   起動時の両方で検出し、UI が壊れた表を静かに出さないようにする
 *   (feedback_silent_failure_worse_than_missing_feature)。
 * @failure: 不整合があれば `AttainmentModelError` を throw する。message にはどの
 *   要素のどの参照が壊れているかを含める (UI には出さず、開発者向け)。
 */

import {
  LEVEL_IDS,
  PREPARATION_KINDS,
  type Assessment,
  type AttainmentModel,
  type CalibrationPoint,
  type Domain,
  type Level,
  type LevelId,
  type Preparation,
  type PreparationKind,
  type Stage,
  type StageExit,
  type StageFocus,
  type Unit,
} from "./types";

export class AttainmentModelError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AttainmentModelError";
  }
}

function fail(message: string): never {
  throw new AttainmentModelError(message);
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function str(obj: Record<string, unknown>, key: string, where: string): string {
  const v = obj[key];
  if (typeof v !== "string" || v.length === 0) {
    fail(`${where}: "${key}" must be a non-empty string`);
  }
  return v;
}

function optStr(obj: Record<string, unknown>, key: string, where: string): string | undefined {
  const v = obj[key];
  if (v === undefined || v === null) return undefined;
  if (typeof v !== "string") fail(`${where}: "${key}" must be a string when present`);
  return v;
}

function optMonth(obj: Record<string, unknown>, key: string, where: string): number | undefined {
  const v = obj[key];
  if (v === undefined || v === null) return undefined;
  if (typeof v !== "number" || !Number.isInteger(v) || v < 1 || v > 12) {
    fail(`${where}: "${key}" must be an integer month 1..12 when present`);
  }
  return v;
}

function strList(obj: Record<string, unknown>, key: string, where: string): string[] {
  const v = obj[key];
  if (v === undefined || v === null) return [];
  if (!Array.isArray(v) || !v.every((x) => typeof x === "string")) {
    fail(`${where}: "${key}" must be a list of strings`);
  }
  return v as string[];
}

function strMap(obj: Record<string, unknown>, key: string, where: string): Record<string, string> {
  const v = obj[key];
  if (v === undefined || v === null) return {};
  if (!isRecord(v)) fail(`${where}: "${key}" must be a map`);
  const out: Record<string, string> = {};
  for (const [k, val] of Object.entries(v)) {
    if (typeof val !== "string") fail(`${where}: "${key}.${k}" must be a string`);
    out[k] = val;
  }
  return out;
}

/** focus は文字列 1 件か、文字列 / `{ lead, text }` のリスト。 */
function focusList(obj: Record<string, unknown>, key: string, where: string): StageFocus[] {
  const v = obj[key];
  if (typeof v === "string") {
    if (v.length === 0) fail(`${where}: "${key}" must not be empty`);
    return [{ text: v }];
  }
  if (!Array.isArray(v) || v.length === 0) fail(`${where}: "${key}" must be a non-empty string or list`);
  return v.map((item, i) => {
    if (typeof item === "string") {
      if (item.length === 0) fail(`${where}.${key}[${i}] must not be empty`);
      return { text: item };
    }
    if (!isRecord(item)) fail(`${where}.${key}[${i}] must be a string or a map`);
    const fw = `${where}.${key}[${i}]`;
    const lead = optStr(item, "lead", fw);
    const text = optStr(item, "text", fw);
    const items = strList(item, "items", fw);
    if (text === undefined && items.length === 0) fail(`${fw}: needs "text" or "items"`);
    const f: StageFocus = {};
    if (lead !== undefined) f.lead = lead;
    if (text !== undefined) f.text = text;
    if (items.length > 0) f.items = items;
    return f;
  });
}

/** exit は `{ id, text, units }` の map。id はチェックリストの鍵なので必須・全ステップで一意。 */
function exitList(obj: Record<string, unknown>, key: string, where: string): StageExit[] {
  const v = obj[key];
  if (v === undefined || v === null) return [];
  if (!Array.isArray(v)) fail(`${where}: "${key}" must be a list`);
  return v.map((item, i) => {
    if (!isRecord(item)) fail(`${where}.${key}[${i}] must be a map with id / text`);
    return {
      id: str(item, "id", `${where}.${key}[${i}]`),
      text: str(item, "text", `${where}.${key}[${i}]`),
      units: strList(item, "units", `${where}.${key}[${i}]`),
    };
  });
}

function list(obj: Record<string, unknown>, key: string, where: string): Record<string, unknown>[] {
  const v = obj[key];
  if (!Array.isArray(v) || v.length === 0) fail(`${where}: "${key}" must be a non-empty list`);
  return v.map((item, i) => {
    if (!isRecord(item)) fail(`${where}: "${key}[${i}]" must be a map`);
    return item;
  });
}

function assertUniqueIds(items: { id: string }[], what: string): void {
  const seen = new Set<string>();
  for (const it of items) {
    if (seen.has(it.id)) fail(`duplicate ${what} id: ${it.id}`);
    seen.add(it.id);
  }
}

function isLevelId(v: string): v is LevelId {
  return (LEVEL_IDS as readonly string[]).includes(v);
}

export function parseAttainmentModel(raw: unknown): AttainmentModel {
  if (!isRecord(raw)) fail("model root must be a map");

  const version = raw.version;
  if (typeof version !== "number") fail(`"version" must be a number`);

  const levels: Level[] = list(raw, "levels", "root").map((l, i) => {
    const id = str(l, "id", `levels[${i}]`);
    if (!isLevelId(id)) fail(`levels[${i}]: unknown level id "${id}"`);
    return {
      id,
      label: str(l, "label", `levels[${i}]`),
      description: str(l, "description", `levels[${i}]`),
    };
  });
  assertUniqueIds(levels, "level");
  for (const expectedId of LEVEL_IDS) {
    if (!levels.some((l) => l.id === expectedId)) fail(`levels: missing "${expectedId}"`);
  }

  const isPreparationKind = (v: string): v is PreparationKind =>
    (PREPARATION_KINDS as readonly string[]).includes(v);

  const stages: Stage[] = list(raw, "stages", "root").map((s, i) => {
    const where = `stages[${i}]`;
    const prepsRaw = s.preparations;
    let preparations: Preparation[] = [];
    if (prepsRaw !== undefined && prepsRaw !== null) {
      if (!Array.isArray(prepsRaw)) fail(`${where}: "preparations" must be a list`);
      preparations = prepsRaw.map((item, j) => {
        if (!isRecord(item)) fail(`${where}.preparations[${j}] must be a map`);
        const pw = `${where}.preparations[${j}]`;
        const kind = str(item, "kind", pw);
        if (!isPreparationKind(kind)) fail(`${pw}: unknown kind "${kind}"`);
        const note = optStr(item, "note", pw);
        const prep: Preparation = { id: str(item, "id", pw), label: str(item, "label", pw), kind };
        if (note !== undefined) prep.note = note;
        return prep;
      });
    }
    return {
      id: str(s, "id", where),
      label: str(s, "label", where),
      period: str(s, "period", where),
      grade: str(s, "grade", where),
      focus: focusList(s, "focus", where),
      exit: exitList(s, "exit", where),
      preparations,
    };
  });
  assertUniqueIds(stages, "stage");
  // preparations の id は全ステップ横断で一意 (家庭側チェック状態のキーのため)
  assertUniqueIds(
    stages.flatMap((st) => st.preparations),
    "preparation",
  );
  const stageIds = new Set(stages.map((s) => s.id));

  const domains: Domain[] = list(raw, "domains", "root").map((d, i) => ({
    id: str(d, "id", `domains[${i}]`),
    label: str(d, "label", `domains[${i}]`),
    aim: str(d, "aim", `domains[${i}]`),
  }));
  assertUniqueIds(domains, "domain");
  const domainIds = new Set(domains.map((d) => d.id));

  const assessments: Assessment[] = list(raw, "assessments", "root").map((a, i) => ({
    id: str(a, "id", `assessments[${i}]`),
    label: str(a, "label", `assessments[${i}]`),
  }));
  assertUniqueIds(assessments, "assessment");
  const assessmentIds = new Set(assessments.map((a) => a.id));

  const vocabularyRaw = raw.vocabulary;
  if (!isRecord(vocabularyRaw)) fail(`"vocabulary" must be a map`);
  const vocabulary: Record<string, string[]> = {};
  const taxonomyRefs = new Set<string>();
  for (const [field, units] of Object.entries(vocabularyRaw)) {
    if (!Array.isArray(units) || !units.every((u) => typeof u === "string")) {
      fail(`vocabulary.${field} must be a list of strings`);
    }
    vocabulary[field] = units as string[];
    for (const u of units as string[]) taxonomyRefs.add(`${field}/${u}`);
  }

  const units: Unit[] = list(raw, "units", "root").map((u, i) => {
    const where = `units[${i}]`;
    const id = str(u, "id", where);
    const domain = str(u, "domain", where);
    if (!domainIds.has(domain)) fail(`${where} (${id}): unknown domain "${domain}"`);

    const taxonomy = strList(u, "taxonomy", where);
    for (const t of taxonomy) {
      if (!taxonomyRefs.has(t)) fail(`${where} (${id}): taxonomy "${t}" is not in vocabulary`);
    }

    const unitAssessments = strList(u, "assessments", where);
    for (const a of unitAssessments) {
      if (!assessmentIds.has(a)) fail(`${where} (${id}): unknown assessment "${a}"`);
    }

    const expectedRaw = u.expected;
    if (!isRecord(expectedRaw) || Object.keys(expectedRaw).length === 0) {
      fail(`${where} (${id}): "expected" must be a non-empty map of stage → level`);
    }
    const expected: Record<string, LevelId> = {};
    for (const [stageId, level] of Object.entries(expectedRaw)) {
      if (!stageIds.has(stageId)) fail(`${where} (${id}): unknown stage "${stageId}"`);
      if (typeof level !== "string" || !isLevelId(level)) {
        fail(`${where} (${id}): invalid level for stage "${stageId}": ${String(level)}`);
      }
      expected[stageId] = level;
    }

    const notes = strMap(u, "notes", where);
    for (const stageId of Object.keys(notes)) {
      if (!stageIds.has(stageId))
        fail(`${where} (${id}): notes references unknown stage "${stageId}"`);
    }

    const basis = optStr(u, "basis", where);

    return {
      id,
      label: str(u, "label", where),
      domain,
      taxonomy,
      expected,
      assessments: unitAssessments,
      notes,
      ...(basis !== undefined ? { basis } : {}),
    };
  });
  assertUniqueIds(units, "unit");

  // exit の id は全ステップで一意であること (plan.exit_states のキーになる)
  const exitIds = new Set<string>();
  for (const s of stages) {
    for (const e of s.exit) {
      if (exitIds.has(e.id)) fail(`stages (${s.id}): duplicate exit id "${e.id}"`);
      exitIds.add(e.id);
    }
  }
  // exit が参照する単元は実在し、かつそのステップで扱う単元であること
  // (別のステップの単元を「終わりまでにできていてほしいこと」に書くのは表の矛盾)。
  for (const s of stages) {
    for (const e of s.exit) {
      for (const unitId of e.units) {
        const unit = units.find((u) => u.id === unitId);
        if (!unit) fail(`stages (${s.id}): exit "${e.text}" references unknown unit "${unitId}"`);
        if (unit.expected[s.id] === undefined) {
          fail(`stages (${s.id}): exit "${e.text}" references unit "${unitId}" not covered in this stage`);
        }
      }
    }
  }

  // 全セル (stage × domain) に最低 1 単元があること。空セルは表の欠落として扱う。
  for (const s of stages) {
    for (const d of domains) {
      const has = units.some((u) => u.domain === d.id && u.expected[s.id] !== undefined);
      if (!has) fail(`empty cell: stage "${s.id}" × domain "${d.id}"`);
    }
  }

  const calibrationPoints: CalibrationPoint[] = list(raw, "calibration_points", "root").map(
    (c, i) => {
      const where = `calibration_points[${i}]`;
      const stage = str(c, "stage", where);
      if (!stageIds.has(stage)) fail(`${where}: unknown stage "${stage}"`);
      const outcome = optStr(c, "outcome", where);
      const month = optMonth(c, "month", where);
      const cp: CalibrationPoint = {
        id: str(c, "id", where),
        label: str(c, "label", where),
        stage,
        timing: str(c, "timing", where),
        measures: str(c, "measures", where),
        method: str(c, "method", where),
        criterion: str(c, "criterion", where),
        notes: strList(c, "notes", where),
      };
      if (outcome !== undefined) cp.outcome = outcome;
      if (month !== undefined) cp.month = month;
      return cp;
    },
  );
  assertUniqueIds(calibrationPoints, "calibration point");

  return {
    version,
    id: str(raw, "id", "root"),
    title: str(raw, "title", "root"),
    scope: str(raw, "scope", "root"),
    principles: strList(raw, "principles", "root"),
    levels,
    stages,
    domains,
    assessments,
    units,
    calibrationPoints,
    vocabulary,
  };
}

/** 指定セル (stage × domain) の単元を YAML の記述順で返す。 */
export function unitsInCell(model: AttainmentModel, stageId: string, domainId: string): Unit[] {
  return model.units.filter((u) => u.domain === domainId && u.expected[stageId] !== undefined);
}

export function levelById(model: AttainmentModel, id: LevelId): Level {
  const found = model.levels.find((l) => l.id === id);
  if (!found) fail(`level not found: ${id}`);
  return found;
}
